import React from "react";
import { Handle, Position } from "@xyflow/react";
import PropTypes from "prop-types";

/**
 * 画布上的节点壳。读取 data.action 对应的 schema，按第一 component.type
 * 决定 body 概要展示什么（[method] url、case 名、data source 名）。
 * 不感知 component 细节。
 */
function ActionNode({ data, selected }) {
  const d = data;
  const schema = d.schema?.actions?.[d.action];

  if (!schema) {
    return (
      <div className="tfe-node">
        <Handle type="target" position={Position.Top} />
        <div className="tfe-node-header">
          <div className="tfe-node-title">未知 action: {d.action}</div>
        </div>
        <Handle type="source" position={Position.Bottom} />
      </div>
    );
  }

  // 用例元信息节点（isInfo=True）：便签纸样式，无 Handle，不能连线
  if (schema.isInfo) {
    const fields = schema.components?.[0]?.fields ?? [];
    const values = d.componentData?.[schema.components?.[0]?.name] ?? {};
    const name = values.name || values[fields[0]?.key] || "未命名用例";
    const descField = fields[1];
    const desc = descField ? values[descField.key] || "" : "";
    return (
      <div className={`tfe-node tfe-node-case-info ${selected ? "selected" : ""}`}>
        <div className="tfe-node-header">
          <div
            className="tfe-node-icon"
            style={{ color: schema.color, backgroundColor: schema.bg }}
          >
            {schema.icon}
          </div>
          <div className="tfe-node-title">{schema.label}</div>
        </div>
        <div className="tfe-node-body tfe-case-info-body">
          <div className="tfe-case-info-name">{name}</div>
          {desc && <div className="tfe-case-info-desc">{desc}</div>}
        </div>
      </div>
    );
  }

  // body 概要：读 componentData[第一 component.name]，按第一 component.type 分发
  const firstComponent = schema.components?.[0];
  const firstValues = d.componentData?.[firstComponent?.name];
  let summary = "";
  if (firstComponent?.type === "api_request_line") {
    const v = (firstValues && typeof firstValues === "object") ? firstValues : {};
    if (v.method && v.url) {
      summary = `[${v.method}] ${v.url}`;
    }
  } else if (firstComponent?.type === "case_call_target") {
    const field = firstComponent.fields?.[0];
    const id = (firstValues && typeof firstValues === "object") ? (firstValues[field?.key] ?? "") : "";
    const opt = field?.options?.find((o) => o.value === id);
    summary = opt?.name ?? id;
  } else if (firstComponent?.type === "data_source_target") {
    const field = firstComponent.fields?.[0];
    const id = (firstValues && typeof firstValues === "object") ? (firstValues[field?.key] ?? "") : "";
    const opt = field?.options?.find((o) => o.value === id);
    summary = opt?.name ?? id;
  } else if (firstComponent?.type === "single_input" || firstComponent?.type === "single_select") {
    // 摘要支持非字符串：数字 / 布尔 / 对象 / 数组
    const cd = d.componentData;
    if (typeof cd === "string") summary = cd;
    else if (typeof cd === "number" || typeof cd === "boolean") summary = String(cd);
    else if (cd && typeof cd === "object") summary = JSON.stringify(cd);
    else summary = "";
  }

  return (
    <div className={`tfe-node ${selected ? "selected" : ""}`}>
      <Handle type="target" position={Position.Top} />
      <div className="tfe-node-header">
        <div
          className="tfe-node-icon"
          style={{ color: schema.color, backgroundColor: schema.bg }}
        >
          {schema.icon}
        </div>
        <div className="tfe-node-title">{schema.label}</div>
      </div>
      <div className="tfe-node-body">{summary}</div>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
}

ActionNode.propTypes = {
  data: PropTypes.shape({
    action: PropTypes.string,
    phase: PropTypes.string,
    componentData: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    schema: PropTypes.object,
  }).isRequired,
  selected: PropTypes.bool,
};

export default ActionNode;
